import React from "react";

const UserTrackHistoryModal = ({ data = [], user, onClose }) => {
  // ✅ Get achievement value by key
  const getAchievementValue = (achievementProgress, key) => {
    if (!achievementProgress) return 0;
    return achievementProgress[key]?.completed || 0;
  };

  return (
    <div className="history-overlay">
      <div className="history-modal">

        {/* HEADER */}
        <div className="header">
          <div>
            <h3>{user?.name || "User"}</h3>
            <p className="sub">KPI History</p>
          </div>
          <span onClick={onClose} className="close-btn">✕</span>
        </div>

        {/* SCROLL AREA */}
        <div className="timeline-scroll">
          <div className="timeline">

            {data.length === 0 ? (
              <div className="empty-history">
                <div className="empty-icon">📭</div>
                <div className="empty-title">No History Found</div>
                <div className="empty-sub">
                  This user has no ambassador activity yet
                </div>
              </div>
            ) : (
              data.map((item, i) => {
                // ✅ Calculate achievement progress from achievementProgress
                const achievementProgress = item.achievementProgress || {};
                const totalAchievements = Object.keys(achievementProgress).length;
                const completedAchievements = Object.values(achievementProgress).filter(
                  ach => ach.isCompleted || ach.completed >= ach.required
                ).length;
                const achievementCompletion = totalAchievements > 0 
                  ? Math.round((completedAchievements / totalAchievements) * 100) 
                  : 0;

                // ✅ Get values from achievementProgress
                const checkins = getAchievementValue(achievementProgress, 'check_ins');
                const uploads = getAchievementValue(achievementProgress, 'media_uploads');
                const referrals = getAchievementValue(achievementProgress, 'referrals');

                return (
                  <div key={i} className="timeline-item">

                    {/* LEFT DOT + LINE */}
                    <div className="timeline-left">
                      <div className={`dot ${item.status?.toLowerCase()}`}></div>
                      {i !== data.length - 1 && <div className="line"></div>}
                    </div>

                    {/* CONTENT */}
                    <div className="content">

                      {/* TITLE */}
                      <div className="title">
                        {`Cycle ${item.cycleNumber || i + 1}`}
                        <span className={`badge ${item.status?.toLowerCase()}`}>
                          {item.status || "Unknown"}
                        </span>
                      </div>

                      {/* DATE */}
                      <div className="date">
                        {item.startDate
                          ? new Date(item.startDate).toLocaleDateString()
                          : "-"} → {
                          item.endDate
                            ? new Date(item.endDate).toLocaleDateString()
                            : "Now"
                        }
                      </div>

                      {/* 🔥 KPI COUNTS - From achievementProgress */}
                      <div className="kpi-stats">
                        <span>📍 {checkins} Check-ins</span>
                        <span>📸 {uploads} Uploads</span>
                        <span>👥 {referrals} Referrals</span>
                      </div>

                      {/* ACHIEVEMENT PROGRESS */}
                      {totalAchievements > 0 && (
                        <div className="achievement-progress">
                          <div className="achievement-label">
                            🏆 Achievements: {completedAchievements}/{totalAchievements}
                          </div>
                          <div className="mini-progress-bar">
                            <div 
                              className="mini-progress-fill"
                              style={{ width: `${achievementCompletion}%` }}
                            />
                          </div>
                          <div className="achievement-percent">
                            {achievementCompletion}% Complete
                          </div>
                        </div>
                      )}

                      {/* STATUS */}
                      <div className={`status ${item.status?.toLowerCase()}`}>
                        {item.status}
                      </div>

                    </div>

                  </div>
                );
              })
            )}

          </div>
        </div>

      </div>

      <style>{`
        .history-overlay {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: rgba(0,0,0,0.75);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 9999;
        }

        .history-modal {
          background: linear-gradient(145deg, #1e1e1e, #262626);
          border-radius: 16px;
          width: 460px;
          max-height: 80vh;
          display: flex;
          flex-direction: column;
          overflow: hidden;
          box-shadow: 0 10px 40px rgba(0,0,0,0.6);
        }

        .header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px 20px;
          border-bottom: 1px solid rgba(255,255,255,0.05);
          position: sticky;
          top: 0;
          background: #1e1e1e;
          z-index: 2;
        }

        .header h3 {
          color: #fff;
          margin: 0;
          font-size: 18px;
        }

        .sub {
          font-size: 12px;
          color: #94a3b8;
          margin-top: 2px;
        }

        .close-btn {
          cursor: pointer;
          font-size: 20px;
          color: #94a3b8;
          transition: color 0.3s;
        }

        .close-btn:hover {
          color: #fff;
        }

        .timeline-scroll {
          overflow-y: auto;
          max-height: 60vh;
          padding: 16px 20px;
        }

        .timeline-item {
          display: flex;
          gap: 14px;
          position: relative;
        }

        .timeline-left {
          display: flex;
          flex-direction: column;
          align-items: center;
          width: 20px;
        }

        .dot {
          width: 12px;
          height: 12px;
          border-radius: 50%;
          flex-shrink: 0;
          margin-top: 4px;
        }

        .dot.active {
          background: #22c55e;
        }

        .dot.expired {
          background: #ef4444;
        }

        .dot.completed {
          background: #22c55e;
        }

        .dot.failed {
          background: #f59e0b;
        }

        .dot.inactive {
          background: #94a3b8;
        }

        .line {
          width: 2px;
          flex: 1;
          background: rgba(255,255,255,0.08);
          margin-top: 4px;
          min-height: 20px;
        }

        .content {
          padding-bottom: 24px;
          flex: 1;
        }

        .title {
          color: #fff;
          font-weight: 600;
          display: flex;
          gap: 8px;
          align-items: center;
          flex-wrap: wrap;
        }

        .badge {
          font-size: 10px;
          padding: 2px 10px;
          border-radius: 10px;
          font-weight: 500;
        }

        .badge.active {
          background: rgba(34, 197, 94, 0.2);
          color: #22c55e;
        }

        .badge.expired {
          background: rgba(239, 68, 68, 0.2);
          color: #ef4444;
        }

        .badge.completed {
          background: rgba(34, 197, 94, 0.2);
          color: #22c55e;
        }

        .badge.failed {
          background: rgba(245, 158, 11, 0.2);
          color: #f59e0b;
        }

        .badge.inactive {
          background: rgba(148, 163, 184, 0.2);
          color: #94a3b8;
        }

        .date {
          font-size: 12px;
          color: #94a3b8;
          margin-top: 2px;
        }

        .kpi-stats {
          display: flex;
          flex-wrap: wrap;
          gap: 12px;
          font-size: 12px;
          margin-top: 8px;
          color: #cbd5f5;
        }

        .kpi-stats span {
          background: rgba(255,255,255,0.04);
          padding: 2px 10px;
          border-radius: 4px;
        }

        .achievement-progress {
          margin-top: 8px;
          padding: 8px 12px;
          background: rgba(255,255,255,0.03);
          border-radius: 6px;
        }

        .achievement-label {
          font-size: 11px;
          color: #94a3b8;
          margin-bottom: 4px;
        }

        .mini-progress-bar {
          width: 100%;
          height: 4px;
          background: rgba(255,255,255,0.06);
          border-radius: 2px;
          overflow: hidden;
        }

        .mini-progress-fill {
          height: 100%;
          background: linear-gradient(90deg, #3b82f6, #8b5cf6);
          border-radius: 2px;
          transition: width 0.3s ease;
        }

        .achievement-percent {
          font-size: 10px;
          color: #94a3b8;
          margin-top: 3px;
        }

        .status {
          font-size: 11px;
          margin-top: 6px;
          font-weight: 500;
        }

        .status.active {
          color: #22c55e;
        }

        .status.expired {
          color: #ef4444;
        }

        .status.completed {
          color: #22c55e;
        }

        .status.failed {
          color: #f59e0b;
        }

        .status.inactive {
          color: #94a3b8;
        }

        .empty-history {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          height: 200px;
          text-align: center;
          color: #94a3b8;
        }

        .empty-icon {
          font-size: 40px;
          margin-bottom: 12px;
        }

        .empty-title {
          font-size: 16px;
          color: #fff;
          font-weight: 600;
        }

        .empty-sub {
          font-size: 13px;
          margin-top: 4px;
          color: #94a3b8;
        }

        @media (max-width: 480px) {
          .history-modal {
            width: 95%;
            max-height: 90vh;
          }
        }
      `}</style>
    </div>
  );
};

export default UserTrackHistoryModal;